import { useState, useMemo } from 'react';
import { ExportDropdown, FilterChip, TableEmptyState, SearchBar } from '@practium/ui';
import type { FilterGroup } from '@practium/ui';
import type { FieldDiaryEntry } from '../../types';
import { InsertionRowSkeleton } from './InsertionRowSkeleton';
import styles from './InsertionList.module.css';

const SKELETON_ROWS = 6;

const STATUS_DOT_CLASS: Record<string, string> = {
	pending: styles.dotPending,
	completed: styles.dotDone,
	upcoming: styles.dotUpcoming,
	note: styles.dotNote,
};

const STATUS_ROW_BG_CLASS: Record<string, string> = {
	pending: styles.rowBgPending,
	completed: styles.rowBgDone,
	upcoming: styles.rowBgUpcoming,
	note: styles.rowBgNote,
};

const FILTER_GROUPS: FilterGroup[] = [
	{
		label: 'Status',
		options: [
			{ value: 'pending', label: 'Pendente' },
			{ value: 'completed', label: 'Realizado' },
			{ value: 'upcoming', label: 'A Vencer' },
			{ value: 'note', label: 'Nota' },
		],
	},
];

export interface InsertionListProps {
	entries: FieldDiaryEntry[];
	isLoading?: boolean;
	onEntryClick?: (entry: FieldDiaryEntry) => void;
	onExportExcel?: () => void;
	onExportPdf?: () => void;
}

export const InsertionList = ({
	entries,
	isLoading = false,
	onEntryClick,
	onExportExcel = () => {},
	onExportPdf = () => {},
}: InsertionListProps) => {
	const [searchQuery, setSearchQuery] = useState('');
	const [filterValues, setFilterValues] = useState<string[]>([]);

	const filtered = useMemo(() => {
		let result = entries;

		if (filterValues.length > 0) {
			result = result.filter((e) => filterValues.includes(e.status));
		}

		if (searchQuery.trim()) {
			const q = searchQuery.toLowerCase();
			result = result.filter(
				(e) => e.title.toLowerCase().includes(q) || e.subtitle.toLowerCase().includes(q)
			);
		}

		return result;
	}, [entries, filterValues, searchQuery]);

	return (
		<div className={styles.card}>
			<div className={styles.header}>
				<span className={styles.title}>Lista de Inserções</span>
				<ExportDropdown
					onExportExcel={onExportExcel}
					onExportPdf={onExportPdf}
					buttonClassName={styles.exportBtn}
				/>
			</div>

			<div className={styles.toolbar}>
				<FilterChip
					values={filterValues}
					onChange={setFilterValues}
					groups={FILTER_GROUPS}
					placeholder="Filtros"
				/>
				<div className={styles.searchWrapper}>
					<SearchBar
						value={searchQuery}
						onChange={setSearchQuery}
						placeholder="Buscar inserção..."
					/>
				</div>
			</div>

			<div className={styles.list}>
				{isLoading
					? Array.from({ length: SKELETON_ROWS }, (_, index) => (
							<InsertionRowSkeleton key={index} />
						))
					: filtered.map((entry) => (
							<div
								key={entry._id}
								className={`${styles.row} ${onEntryClick ? styles.rowClickable : ''}`}
								onClick={() => onEntryClick?.(entry)}
							>
								<span className={`${styles.dot} ${STATUS_ROW_BG_CLASS[entry.status]}`}>
									<span className={`${styles.dotIn} ${STATUS_DOT_CLASS[entry.status]}`} />
								</span>
								<div className={styles.rowContent}>
									<span className={styles.rowTitle}>{entry.title}</span>
									<span className={styles.rowSubtitle}>{entry.subtitle}</span>
								</div>
							</div>
						))}
				{!isLoading && filtered.length === 0 && (
					<div className={styles.empty}>
						<TableEmptyState
							title="Nenhuma inserção encontrada"
							subtitle={
								entries.length === 0
									? 'Insira algo para aparecer aqui'
									: 'Tente ajustar os filtros ou a busca'
							}
						/>
					</div>
				)}
			</div>
		</div>
	);
};
